<template>
  <div class="mt8 pa8">
    <CardTemplate @show-modal="showDialog">
      <template #icon>
        <v-img
          class="account-img"
          src="./assets/update-comm.svg"
          alt="communications preferences"
          width="46px"
          height="46px"
        />
      </template>
      <template #title>
        {{ $t('L_investment_statement_pre') }}
      </template>
      <template #content>
        <p
          :class="
            !$vuetify.display.xs
              ? 'd-flex align-center justify-center mt-4'
              : 'd-block text-left mt-0 ml-2 pb-4 pl-16'
          "
        >
          {{ $t('L_investment_statement_post') }}
        </p>
      </template>
      <template #action>
        <OnexButton
          @click="showDialog"
          variant="text"
          id="update_paperless_trigger"
          :aria-label="$t('L_set_communication')"
        >
          {{ $t('L_edit') }}
        </OnexButton>
      </template>
    </CardTemplate>

    <MyAccountDialog
      :header="$t('L_investment_statement_title')"
      :show="showInvestmentDialog"
      :max-width="600"
      @save="save"
      @close="closeAndGoToPreviousScreen"
      :close-is-active="closeIsActive"
      :enable-save-button="enableSaveButton && !isSaveInProgress"
    >
      <v-row>
        <v-col cols="12">
          <p v-if="commPreference === 'MAIL'">
            {{ $t('L_with_paperless_only_selected_as_your_prefered_document_delivery') }}
          </p>
          <p v-else>
            {{ $t('L_if_you_select_the_paperless_option_as_your_preferred_mutual_fund') }}
          </p>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12">
          {{ $t('L_preferred_mutual_fund_account_document_delivery_preference') }}
        </v-col>
        <v-col cols="12">
          <v-radio-group
            row
            v-model="commPreference"
            @change="changed = true"
            class="divInlineBlock"
          >
            <OnexRadio :name="'radio_mail'" value="MAIL" :label="$t('L_mail_only')" />
            <OnexRadio :name="'radio_digital'" value="DIGITAL" :label="$t('L_paperless_only')" />
          </v-radio-group>
        </v-col>
      </v-row>
      <v-row wrap v-if="commPreference === 'MAIL'" class="mt-n6">
        <v-col cols="12" class="pb-0">
          {{ $t('L_mailed_documents_will_be_sent_to') }}
        </v-col>
        <v-col cols="12" class="mb-0 pt-0 pb-0">
          <div v-for="(address, index) in getAddresses" :key="index" class="mt-4 mb-4">
            <div>{{ address.address1 }}</div>
            <div>{{ address.address2 }}</div>
            <div>{{ address.city }} {{ address.province }} {{ address.postalCode }}</div>
            <div class="mb-4">
              {{ address.country }}
            </div>
          </div>
        </v-col>
        <v-col cols="12" class="mb-4">
          <span>{{ $t('L_if_this_address_is_incorrect_please_reach_out_to') }}</span>
        </v-col>
      </v-row>
      <v-row wrap v-else-if="commPreference === 'DIGITAL'" class="mt-n6">
        <v-col cols="12" class="pb-4">
          {{ $t('L_paperless_document_notifications_will_be_sent_to') }}
          <span class="link"> {{ customerProfile.email }}</span>
        </v-col>
        <v-col cols="12" class="pb-4">
          {{ $t('L_if_you_would_like_to_update_this_email_address_you_may_do_so_via_your') }}
        </v-col>
        <v-col cols="12" class="mb-4">
          {{ $t('L_by_selecting_paperless_only_I_agree_to_the') }}
          <span @click="openElectronicDeliveryDisclosure" class="link">{{
            $t('L_electronics_delivery_disclosure')
          }}</span>
        </v-col>
      </v-row>
    </MyAccountDialog>
    <MessageDialog
      :show-dialog="dialog"
      :has-button-two="true"
      :is-persistent="true"
      :type="'info'"
      :title="$t('L_are_you_sure_you_would_like_to_cancel')"
      :message="''"
      :button-two-label="$t('L_cancel')"
      :button-one-label="$t('L_confirm')"
      @buttonTwoAction="dialog = false"
      @buttonOneAction="forcesave"
    >
      <template v-slot:message>
        <div class="pl-9">
          <p class="fontRegular">
            {{ $t('L_paperless_document_offers') }}
          </p>
          <p class="font-weight-bold">
            {{ $t('L_convenience') }}
          </p>
          <ul class="pl-8">
            <li class="fontRegular">
              {{ $t('L_access_any_time_any_where') }}
            </li>
            <li class="fontRegular">
              {{ $t('L_print_or_archive') }}
            </li>
          </ul>
          <p class="font-weight-bold">
            {{ $t('L_security') }}
          </p>
          <ul class="pl-8">
            <li>{{ $t('L_never_lost_in_the_mail') }}</li>
          </ul>
        </div>
      </template>
    </MessageDialog>
    <v-dialog
      v-model="isConfirmationDialogOpen"
      :fullscreen="$vuetify.display.xs"
      max-width="600"
      z-index="10000"
    >
      <v-card class="update_user_mobile_modal">
        <v-card-title class="pb-0">
          <v-row class="text-right modal-primary-title">
            <v-col>
              <v-icon
                :size="$vuetify.display.xs ? '22' : '26'"
                @click="isConfirmationDialogOpen = false"
                class="btn-close"
                color="#333333"
                :icon="mdiClose"
              />
              <h2 class="text-h2 text-center pb-2" aria-live="assertive">
                {{ $t('L_paperless_communication_changes_saved') }}
              </h2>
            </v-col>
          </v-row>
        </v-card-title>
        <v-card-text>
          <v-row>
            <v-col offset-md="2" md="8" offset="1" cols="10" class="text-center">
              <p>{{ $t('L_paperless_communication_updated') }}</p>
              <OnexButton
                block
                color="primary"
                class="mb-3 mt-5 px-2"
                @click="isConfirmationDialogOpen = false"
              >
                {{ $t('L_paperless_communication_ok') }}
              </OnexButton>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent, type ComponentOptions } from 'vue';
import { useGlobalStore } from '@/stores/global/global.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { mapState, mapStores } from 'pinia';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { OnexButton, OnexRadioGroup, OnexRadio } from '@primerica/onex-ui-lib';
import bff from '@/shared/api/bff';
import MyAccountDialog from './MyAccountDialog.vue';
import CardTemplate from './CardTemplate.vue';
import { mdiClose, mdiHelp } from '@mdi/js';
import MessageDialog from '@/components/MessageDialog.vue';
import { openInNewWindow } from '@/mixins/openNewWindow';

const componentOptions: ComponentOptions = {
  name: 'UpdateInvestmentCommunication',
  components: {
    CardTemplate,
    MyAccountDialog,
    OnexButton,
    OnexRadioGroup,
    OnexRadio,
    MessageDialog,
  },
  props: {
    invdata: {
      type: [Object],
      required: false, // TODO true
    },
  },
  data: () => ({
    dialog: false,
    showInvestmentDialog: false,
    closeIsActive: false,
    changed: false,
    headers: [
      {
        text: '',
        value: 'contractId',
      },
      {
        text: 'Online & Mail',
      },
      {
        text: 'Online Only',
      },
    ],
    selections: {},
    preferences: [],
    originalChannel: '',
    commPreference: '',
    isConfirmationDialogOpen: false,
    isSaveInProgress: false,
    forceSave: false, // Used for confirming if previously DIGITAL now selecting MAIL
    mdiHelp,
    mdiClose,
  }),
  computed: {
    ...mapStores(useGlobalStore, usePolicyStore),
    ...mapState(useCustomerStore, ['customerProfile']),
    ...mapState(usePolicyStore, ['policies']),
    ...mapState(useGlobalStore, {
      locale: 'getLocale',
    }),
    getAddresses() {
      return this.invdata?.addresses || [];
    },
    enableSaveButton() {
      return this.commPreference !== '' && this.changed;
    },
    disclosureDocument() {
      let urlToShow;
      if (this.locale.indexOf('en_CA') !== -1) {
        urlToShow =
          'https://assets.contentstack.io/v3/assets/blt1dedeb682e8df2a2/blt38e4792640ee26f5/ecommunication_en_CA.pdf';
      } else if (this.locale.indexOf('fr_CA') !== -1) {
        urlToShow =
          'https://assets.contentstack.io/v3/assets/blt1dedeb682e8df2a2/bltf9257bbc10967b43/ecommunication_fr_CA.pdf';
      } else {
        urlToShow =
          'https://assets.contentstack.io/v3/assets/blt1dedeb682e8df2a2/blt56f08e892f0cc5b0/ecommunication_en_US.pdf';
      }
      return urlToShow;
    },
  },
  mixins: [openInNewWindow],
  methods: {
    closeAndGoToPreviousScreen() {
      // This is the one that actually closes the Investment Statement Dialog
      this.dialog = false;
      this.showInvestmentDialog = false;
      this.closeIsActive = false;
    },
    async openElectronicDeliveryDisclosure() {
      let urlToShow;
      if (this.locale.indexOf('en_CA') !== -1) {
        urlToShow =
          'https://assets.contentstack.io/v3/assets/blt1dedeb682e8df2a2/blt38e4792640ee26f5/ecommunication_en_CA.pdf';
      } else if (this.locale.indexOf('fr_CA') !== -1) {
        urlToShow =
          'https://assets.contentstack.io/v3/assets/blt1dedeb682e8df2a2/bltf9257bbc10967b43/ecommunication_fr_CA.pdf';
      } else {
        urlToShow =
          'https://assets.contentstack.io/v3/assets/blt1dedeb682e8df2a2/blt56f08e892f0cc5b0/ecommunication_en_US.pdf';
      }
      // const respUrl = Config.get('');
      // TODO change to correct one once we get from Business.
      const isWeb = this.$pc2capacitor.isWeb();
      await this.openInNewWindow({ url: urlToShow, target: '_blank', inappbrowser: !isWeb });
    },
    forcesave() {
      this.forceSave = true;
      this.dialog = false;
      this.save();
    },
    async showDialog() {
      this.preferences = await bff.getInvestmentsCommunicationsPreferences();
      // This is needed to reset data if user happens to change radio button, close the dialog and come back here
      this.commPreference = this.preferences?.channel || '';
      this.showInvestmentDialog = true;
      this.forceSave = false;
    },
    async save() {
      if (
        this.originalChannel === 'DIGITAL' &&
        this.commPreference === 'MAIL' &&
        this.forceSave === false
      ) {
        // This is a confirmation dialog to ensure the user really want to close
        this.dialog = true;
      } else {
        try {
          this.isSaveInProgress = true;
          const reqPayloadObj = {
            investment: { channel: this.commPreference },
            source: 'AS', // AS: Account Settings
          };
          // const changedSelections = {};
          // this.preferences.forEach((preference) => {
          //   if (this.selections[preference.policyNumber] !== preference.channel) {
          //     changedSelections[preference.policyNumber] = this.selections[preference.policyNumber];
          //   }
          // });
          /*
           * Emit a custom action when a user successfully submits an update paperless settings request
           * https://www7.v1host.com/Primerica/attachment.img/284635/Analytics%20User%20Stories%20DT_3.30.21.docx
           */
          const i = this.$dynatrace.enterAction(
            'account_settings_changes_saved_paperless_settings'
          );
          await bff.updateInvestmentPaperless(reqPayloadObj, this.locale);
          this.originalChannel = this.commPreference;
          this.$dynatrace.leaveAction(i);
          this.isConfirmationDialogOpen = true;
          this.showInvestmentDialog = false;
        } catch (e) {
          this.$store.commit('addError', { error: e, context: 'myaccount' });
          this.isSaveInProgress = false;
          return;
        }
        this.isSaveInProgress = false;
        this.closeAndGoToPreviousScreen();
      }
    },
  },
  async mounted() {
    //     "channel": "MAIL",
    //     "addresses": [
    //         {
    //             "address1": "123 Main Street",
    //             "address2": "Suite 100",
    //             "city": "Toronto",
    //             "province": "ON",
    //             "country": "CA",
    //             "postalCode": "M5V 1H1"
    //         },
    //         {
    //             "address1": "456 Main Street",
    //             "address2": "Suite 100",
    //             "city": "Toronto2",
    //             "province": "ON",
    //             "country": "CA",
    //             "postalCode": "M5V 2H2"
    //         }
    //     ]
    // preferences call is used to see if investment exists to show investmentCommEnabled
    const preferences = await bff.getInvestmentsCommunicationsPreferences();
    if (preferences !== undefined) {
      // TODO change to preferences.investments
      this.commPreference = preferences?.channel || '';
      this.originalChannel = this.commPreference;
    }
    // const clickTarget = this.$el.querySelector('.clickable');
    // if (clickTarget) {
    //   clickTarget.addEventListener('click', this.showDialog.bind(this));
    // }
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
a[disabled] {
  color: rgba(0, 0, 0, 0.54) !important;
  cursor: default !important;
}

.policyHeader {
  width: 70px !important;
  margin-right: 20px;
}

.link {
  color: #02579e;
  font-weight: 500;
  cursor: pointer;
}
a[disabled] {
  color: rgba(0, 0, 0, 0.54) !important;
  cursor: default !important;
}
.v-input--selection-controls {
  margin-top: 2px !important;
}
.policyData {
  width: 105px !important;
  margin-right: 24px;
}

.divInlineBlock {
  display: inline-block;
}
.v-input {
  padding-bottom: 4px !important;
}

.fontRegular {
  font-weight: 400;
}
</style>
